import React, { useMemo } from "react";
import styled from "styled-components";
import { formatDate } from "@backpacker/date";
import Cloud from "@charty/cloud";
import Emoji from "@charty/cloud/emoji";
import Word from "@charty/cloud/word";
import { Facet } from "@linkfluence/models/facet";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { tr } from "@radarly/i18n";
import { hideableTerms } from "@radarly/models/cards/term";
import { DatavizCloud } from "@radarly/models/dataviz";
import { FullFacet } from "@radarly/models/facets";
import { SearchFragmentTerm } from "@radarly/models/search";
import { useRangeFromFragment } from "../../data";
import Empty from "../../empty";
import Tooltip from "../../tooltip/metric";
import { getSeries } from "../data";

const Container = styled.div`
  height: 100%;
`;

const LIMIT = 30;

export interface SharedWidgetsTermCloudInnerProps {
  loading: boolean;
  data: SearchFragmentTerm["data"];
  fragment: SearchFragmentTerm;
  dataviz: DatavizCloud;
  onClickItem?: (
    uid: string | number,
    status?: "include" | "exclude",
    label?: string
  ) => void;
  className?: string;
  facet: FullFacet;
  disableAnimation?: boolean;
  isEdition?: boolean;
}

export default function SharedWidgetsTermCloudInner({
  loading,
  data,
  fragment,
  dataviz,
  onClickItem,
  className,
  facet,
  disableAnimation,
  isEdition,
}: SharedWidgetsTermCloudInnerProps) {
  const locale = useCurrentUserLocale();
  const range = useRangeFromFragment(fragment, loading);
  const metric = fragment.request.aggs[facet.id].metrics[0];
  const isHideable = hideableTerms.includes(facet.id as Facet);
  const hiddenTerms = useMemo(
    () => dataviz?.hiddenTerms || [],
    [dataviz?.hiddenTerms]
  );
  const items = useMemo(
    () =>
      getSeries(
        data,
        facet,
        metric,
        locale,
        hiddenTerms,
        isHideable,
        isEdition,
        LIMIT
      ),
    [data, facet, metric, locale, hiddenTerms, isHideable, isEdition]
  );
  return (
    <Empty empty={!items.length}>
      <Container>
        <Cloud
          className={className}
          items={items}
          color={dataviz.color}
          order="label"
          limit={LIMIT}
          minMaxRatio={0.65}
          isHideable={isHideable && isEdition}
          disableAnimation={disableAnimation}
          renderItem={(item, animated) =>
            facet.id === Facet.EmojiCharts ? (
              <Emoji
                label={item.label}
                onClick={onClickItem ? () => onClickItem(item.id) : null}
                normalizedWeight={item.normalizedWeight}
                locale={locale}
                weight={item.weight}
                animated={animated}
                tooltip={(serie) => (
                  <Tooltip
                    range={range}
                    count={serie.weight}
                    label={serie.label}
                    metric={metric}
                    locale={locale}
                  />
                )}
              />
            ) : (
              <Word
                isHidden={item.isHidden}
                onClick={
                  onClickItem
                    ? () =>
                        onClickItem(
                          item.id,
                          item.isHidden ? "exclude" : "include",
                          item.label
                        )
                    : null
                }
                label={item.label}
                color={dataviz.color || item.color}
                normalizedWeight={item.normalizedWeight}
                locale={locale}
                weight={item.weight}
                animated={animated}
                tooltip={(serie) => (
                  <Tooltip
                    range={range}
                    count={serie.weight}
                    label={serie.label}
                    metric={metric}
                    locale={locale}
                    color={facet.id === Facet.Affects ? serie.color : null}
                    footer={
                      isHideable &&
                      isEdition && (
                        <div>
                          <p>
                            {formatDate(range.from, locale, "ll")} -{" "}
                            {formatDate(range.to, locale, "ll")}{" "}
                          </p>
                          <p>
                            {tr(
                              locale,
                              `INSIGHT_PAGE::EDITOR::HIDDENT_TERM_CLICK_${
                                item.isHidden ? "SHOW" : "HIDE"
                              }`
                            )}
                          </p>
                        </div>
                      )
                    }
                  />
                )}
              />
            )
          }
        />
      </Container>
    </Empty>
  );
}
